import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useJob } from "../api/jobs";
import {
  useApprovalSummary,
  useContentGaps,
  useCourses,
  useExtractAll,
  useExtractionRuns,
  useIndexPending,
  useIndexRuns,
  useSyncAll,
  useSyncRuns,
} from "../api/queries";
import { JobNotice } from "../components/JobNotice";
import { SyncDialog } from "../components/SyncDialog";
import { OverviewCard } from "../components/OverviewCard";
import { Card, Empty, ErrorBanner, Loading, RelativeTime } from "../components/ui";
import {
  ArrowRightIcon,
  BarChartIcon,
  CheckIcon,
  CubeIcon,
  DocCheckIcon,
  ExtractIcon,
  FileIcon,
  IndexIcon,
  InfoIcon,
  QueueIcon,
  RefreshIcon,
  SearchIcon,
  SyncIcon,
  TargetIcon,
} from "../components/icons";
import { DonutChart, TrendChart, type Slice, type TrendPoint } from "../components/charts";

// Each card reads its own query (sync, extraction, index, courses, content
// gaps), so one endpoint failing or hanging never blocks the other cards.
export function DashboardPage() {
  const qc = useQueryClient();

  const courses = useCourses();
  const syncRuns = useSyncRuns();
  const extractRuns = useExtractionRuns();
  const indexRuns = useIndexRuns();
  const gaps = useContentGaps();
  const approvals = useApprovalSummary();

  const syncJob = useJob("sync");
  const extractJob = useJob("extract");
  const indexJob = useJob("index");

  const syncAll = useSyncAll();
  const extractAll = useExtractAll();
  const indexPending = useIndexPending();

  const [syncOpen, setSyncOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const lastSync = syncRuns.data?.[0];
  const lastExtract = extractRuns.data?.runs[0];
  const lastIndex = indexRuns.data?.runs[0];
  const pendingApproval = approvals.data?.totals.pending;
  const pendingModules = extractRuns.data?.pending_modules;

  const gapTotal = gaps.data?.recurring_refusals.length;
  const gapLowRated = gaps.data?.low_rated_answers.length;

  async function refresh() {
    setRefreshing(true);
    try {
      await qc.refetchQueries({ type: "active" });
    } finally {
      setRefreshing(false);
    }
  }

  // Donut: where documents currently sit in the review workflow.
  const reviewSlices: Slice[] = useMemo(() => {
    const t = approvals.data?.totals;
    if (!t) return [];
    return [
      { name: "Approved", value: t.approved, color: "#1E9E5A" },
      { name: "Pending", value: t.pending, color: "#f59e0b" },
      { name: "Rejected", value: t.rejected, color: "#dc2626" },
      { name: "Retired", value: t.retired, color: "#9ca3af" },
    ].filter((s) => s.value > 0);
  }, [approvals.data]);

  // Line: documents written by each of the most recent extraction runs.
  const extractionTrend: TrendPoint[] = useMemo(() => {
    return [...(extractRuns.data?.runs ?? [])]
      .sort((a, b) => new Date(a.started_at ?? 0).getTime() - new Date(b.started_at ?? 0).getTime())
      .map((run) => ({
        id: run.id,
        label: run.started_at ? new Date(run.started_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : `#${run.id}`,
        fullLabel: run.started_at ? new Date(run.started_at).toLocaleString() : `Run #${run.id}`,
        documents: run.documents_written,
        failures: run.failures,
      }));
  }, [extractRuns.data]);

  return (
    <div className="p-5 lg:p-6">
      <ErrorBanner error={syncAll.error || extractAll.error || indexPending.error} />
      <JobNotice kind="sync" />
      <JobNotice kind="extract" />
      <JobNotice kind="index" />

      {/* Page header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Overview of your data extraction and processing pipeline
          </p>
        </div>
        <button
          type="button"
          aria-label="Refresh"
          title="Refresh"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="grid h-10 w-10 place-items-center rounded-full border border-[#E4E9F2] bg-white text-slate-500 shadow-sm transition-colors hover:text-accent disabled:opacity-60 dark:border-[#1E2E4D] dark:bg-panel dark:text-slate-300 dark:hover:text-[#79A9FF]"
        >
          <RefreshIcon className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Pipeline overview cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <OverviewCard
          tone="blue"
          icon={<FileIcon className="h-5 w-5" />}
          badge={syncJob ? "Running" : "Success"}
          badgeTone={syncJob ? "partial" : "success"}
          title="Courses Mirrored"
          value={courses.isLoading ? "…" : courses.data?.total ?? "—"}
          caption={lastSync ? <>Last run <RelativeTime iso={lastSync.started_at} /></> : "No syncs yet"}
          footer={<><SyncIcon className="h-4 w-4" /> Sync now</>}
          footerTone="blue"
          footerDisabled={!!syncJob}
          onFooterClick={() => setSyncOpen(true)}
        />

        <OverviewCard
          tone="violet"
          icon={<CubeIcon className="h-5 w-5" />}
          badge={extractJob ? "Running" : pendingModules ? "Partial" : "On track"}
          badgeTone={extractJob || pendingModules ? "partial" : "ontrack"}
          title="Modules Waiting to Extract"
          value={extractRuns.isLoading ? "…" : pendingModules ?? "—"}
          caption={lastExtract ? <>Last run <RelativeTime iso={lastExtract.started_at} /></> : "No extractions yet"}
          footer={<><ExtractIcon className="h-4 w-4" /> Extract</>}
          footerTone="violet"
          footerDisabled={!!extractJob || extractAll.isPending}
          onFooterClick={() => extractAll.mutate()}
        />

        <OverviewCard
          tone="green"
          icon={<DocCheckIcon className="h-5 w-5" />}
          badge={pendingApproval ? "Needs attention" : "On track"}
          badgeTone={pendingApproval ? "attention" : "ontrack"}
          title="Documents Awaiting Approval"
          value={approvals.isLoading ? "…" : pendingApproval ?? "—"}
          caption={approvals.data ? `${approvals.data.totals.approved} approved, ${approvals.data.totals.rejected} rejected` : "—"}
          footer={<><QueueIcon className="h-4 w-4" /> Open queue</>}
          footerTone="green"
          href="/approvals"
        />

        <OverviewCard
          tone="violet"
          icon={<SearchIcon className="h-5 w-5" />}
          badge={indexJob ? "Running" : "Success"}
          badgeTone={indexJob ? "partial" : "success"}
          title="Documents Not Yet Indexed"
          value={indexRuns.isLoading ? "…" : indexRuns.data?.pending_documents ?? "—"}
          caption={lastIndex ? <>Last run <RelativeTime iso={lastIndex.started_at} /></> : "No index runs yet"}
          footer={<><IndexIcon className="h-4 w-4" /> Index approved</>}
          footerTone="blue"
          footerDisabled={!!indexJob || indexPending.isPending}
          onFooterClick={() => indexPending.mutate()}
        />
      </div>

      {/* Charts: what flowed in (extraction) over recent runs, and where it sits now (review). */}
      <div className="mt-6 grid gap-4 min-[1200px]:grid-cols-3">
        <Card
          title={<span className="flex items-center gap-2"><FileIcon className="h-4 w-4 text-accent" /> Document review status</span>}
          actions={<Link to="/approvals" className="text-sm font-medium text-accent hover:underline dark:text-[#79A9FF]">View all</Link>}
        >
          {approvals.error ? (
            <ErrorBanner error={approvals.error} />
          ) : approvals.isLoading ? (
            <Loading />
          ) : reviewSlices.length ? (
            <DonutChart
              data={reviewSlices}
              centerValue={reviewSlices.reduce((sum, s) => sum + s.value, 0).toLocaleString()}
              centerLabel="documents"
            />
          ) : (
            <Empty>No documents yet.</Empty>
          )}
        </Card>
        <Card
          className="min-[1200px]:col-span-2"
          title={
            <span className="flex items-center gap-2">
              <BarChartIcon className="h-4 w-4 text-accent" /> Extraction throughput{" "}
              <span className="font-normal text-gray-400 dark:text-slate-500">documents written per run</span>
            </span>
          }
          actions={<DetailsLink to="/runs?tab=extract" />}
        >
          {extractRuns.error ? (
            <ErrorBanner error={extractRuns.error} />
          ) : extractRuns.isLoading ? (
            <Loading />
          ) : extractionTrend.length >= 2 ? (
            <TrendChart data={extractionTrend} />
          ) : (
            <Empty>Not enough extraction runs to chart yet.</Empty>
          )}
        </Card>
      </div>

      {/* Nothing but content gaps below the charts, matching the dark design. */}
      <div className="mt-6">
        <Card
          title={
            <div>
              <div className="flex items-center gap-2 text-gray-900 dark:text-slate-100">
                <TargetIcon className="h-4 w-4 text-accent" /> Content gaps
              </div>
              <div className="mt-0.5 text-xs font-normal text-slate-400 dark:text-slate-500">
                {gapTotal ?? "—"} recurring unanswered question{gapTotal === 1 ? "" : "s"}
              </div>
            </div>
          }
          actions={<Link to="/content-gaps" className="text-sm font-medium text-accent hover:underline dark:text-[#79A9FF]">View all</Link>}
        >
          {gaps.error ? (
            <ErrorBanner error={gaps.error} />
          ) : (
            <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)] sm:items-center">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E8F0FE] text-[#2767D5] dark:bg-[#1B2E52] dark:text-[#79A9FF]">
                  <FileIcon className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-2xl font-bold leading-none tabular-nums text-slate-900 dark:text-white">{gapTotal ?? "—"}</div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">total gaps</div>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:border-l sm:border-gray-200 sm:pl-5 dark:border-[#1E2E4D]">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#E3F6EC] text-[#1E9E5A] dark:bg-[#102E23] dark:text-[#5FD39A]">
                  <CheckIcon className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-2xl font-bold leading-none tabular-nums text-slate-900 dark:text-white">{gapLowRated ?? "—"}</div>
                  <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">low-rated answers</div>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:border-l sm:border-gray-200 sm:pl-5 dark:border-[#1E2E4D]">
                <InfoIcon className="h-5 w-5 shrink-0 text-slate-400 dark:text-slate-500" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Review and address these gaps to improve extraction quality and coverage.
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* "Sync now" opens the same dry-run preview as the sync button. */}
      {syncOpen && (
        <SyncDialog
          onClose={() => setSyncOpen(false)}
          onSync={(ids) => {
            syncAll.mutate(ids);
            setSyncOpen(false);
          }}
        />
      )}
    </div>
  );
}

function DetailsLink({ to }: { to: string }) {
  return (
    <Link to={to} className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline dark:text-[#79A9FF]">
      Details
      <ArrowRightIcon className="h-4 w-4" />
    </Link>
  );
}
